<template>
  <div class="home">
    <div class="hero">
      <h1>Parrainage Tesla : 1 000 km de Supercharge offerts</h1>
      <p class="subtitle">Commandez votre Model 3 ou Model Y neuve avec mon lien de parrainage officiel et recevez 1 000 km de recharge gratuite sur le réseau Superchargeur. Aucun surcoût pour vous.</p>
      <a :href="referralLink" target="_blank" rel="noopener noreferrer" class="cta-button">⚡ Utiliser le lien de parrainage</a>
      <p class="hero-note">Offre vérifiée le {{ verifiedOn }} · Programme officiel Tesla · Aucun code à saisir</p>
    </div>

    <div class="referral-section">
      <div class="warning">
        <strong>Important :</strong> cliquez sur le lien AVANT de commencer votre commande sur tesla.com. Tesla n'applique pas le parrainage sur une commande déjà passée.
      </div>

      <div class="availability" :class="{ low: referralsLeft <= 3 }">
        <div class="availability-text">
          <strong>{{ referralsLeft > 0 ? 'Parrainage disponible' : 'Plus de parrainage disponible cette année' }}</strong>
          <span>{{ referralsLeft }} / {{ referralsMax }} parrainages restants cette année</span>
        </div>
        <div class="availability-bar" role="progressbar" :aria-valuenow="referralsLeft" aria-valuemin="0" :aria-valuemax="referralsMax">
          <div class="availability-fill" :style="{ width: (referralsLeft / referralsMax) * 100 + '%' }"></div>
        </div>
      </div>

      <div class="steps">
        <h2>Comment ça marche</h2>
        <ol>
          <li><strong>Cliquez sur le lien</strong> ci-dessus : il ouvre tesla.com avec le parrainage déjà associé, rien à saisir.</li>
          <li><strong>Configurez et commandez</strong> votre Tesla comme d'habitude, au même prix.</li>
          <li><strong>Vos 1 000 km de Supercharge</strong> apparaissent dans votre compte Tesla après la livraison.</li>
        </ol>
      </div>

      <div class="benefits">
        <h2>Bon à savoir</h2>
        <ul>
          <li>⚡ 1 000 km de recharge gratuite sur les Superchargeurs Tesla (environ 150 kWh)</li>
          <li>🚗 Model 3 et Model Y neuves uniquement, commandées via le lien</li>
          <li>💶 Aucun surcoût : le prix de votre Tesla ne change pas, et le parrainage se cumule avec les aides publiques</li>
          <li>✅ Un seul parrainage par acheteur, limité à 10 par parrain et par année civile</li>
          <li>⏳ Tesla peut modifier ou arrêter le programme à tout moment : l'offre actuelle n'est pas garantie</li>
        </ul>
      </div>

      <div class="referral-card">
        <h2>Mon lien de parrainage</h2>
        <div class="link-container">
          <input
            ref="linkInput"
            type="text"
            :value="referralLink"
            readonly
            class="referral-link"
            @click="copyToClipboard"
          />
          <button @click="copyToClipboard" class="copy-button" :class="{ copied: isCopied }">
            {{ isCopied ? '✓ Copié !' : 'Copier' }}
          </button>
        </div>
        <p class="link-description">Copiez-le si vous préférez commander plus tard : l'avantage s'applique tant que la commande démarre depuis ce lien.</p>
      </div>

      <p class="trust">Je suis Damien, propriétaire Tesla depuis août 2023, 32 000 km au compteur. Ce lien est mon lien de parrainage personnel : en l'utilisant, vous recevez vos 1 000 km et moi aussi. Merci !</p>

      <div class="faq">
        <h2>Questions fréquentes</h2>
        <details v-for="item in faq" :key="item.question">
          <summary>{{ item.question }}</summary>
          <p>{{ item.answer }}</p>
        </details>
      </div>

      <div class="final-cta">
        <a :href="referralLink" target="_blank" rel="noopener noreferrer" class="cta-button">⚡ Utiliser le lien de parrainage</a>
      </div>

      <div class="share-buttons">
        <h3>Transmettre à un proche</h3>
        <div class="share-options">
          <button @click="shareEmail" class="share-button email">📧 Email</button>
          <button @click="shareWhatsApp" class="share-button whatsapp">💬 WhatsApp</button>
          <button @click="shareTwitter" class="share-button twitter">✖️ X</button>
        </div>
      </div>

      <p class="disclaimer">Site personnel indépendant, sans lien avec Tesla, Inc. La commande se fait exclusivement sur tesla.com ; les conditions du programme de parrainage sont celles publiées par Tesla.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useSEO, type FAQItem } from '../composables/useSEO'


const referralLink = 'https://ts.la/damien430515'
const linkInput = ref<HTMLInputElement | null>(null)
const isCopied = ref(false)

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(referralLink)
    isCopied.value = true
    setTimeout(() => {
      isCopied.value = false
    }, 2000)
  } catch (err) {
    if (linkInput.value) {
      linkInput.value.select()
      document.execCommand('copy')
      isCopied.value = true
      setTimeout(() => {
        isCopied.value = false
      }, 2000)
    }
  }
}

const shareEmail = () => {
  const subject = encodeURIComponent('Parrainage Tesla : 1 000 km de Supercharge offerts')
  const body = encodeURIComponent(`Commande ta Tesla avec ce lien de parrainage et reçois 1 000 km de Supercharge gratuits, sans surcoût :\n\n${referralLink}`)
  window.location.href = `mailto:?subject=${subject}&body=${body}`
}

const shareWhatsApp = () => {
  const text = encodeURIComponent(`1 000 km de Supercharge offerts si tu commandes ta Tesla avec ce lien de parrainage : ${referralLink}`)
  window.open(`https://wa.me/?text=${text}`, '_blank')
}

const shareTwitter = () => {
  const text = encodeURIComponent(`Parrainage Tesla : 1 000 km de Supercharge offerts à la commande ${referralLink}`)
  window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank')
}

const faq: FAQItem[] = [
  {
    question: 'Faut-il saisir un code parrainage Tesla ?',
    answer: 'Non. Chez Tesla le parrainage passe par un lien, pas par un code. Cliquez sur le lien, il ouvre tesla.com avec le parrainage déjà associé à votre commande.'
  },
  {
    question: 'Quand faut-il cliquer sur le lien ?',
    answer: 'Avant de commencer la configuration de votre Tesla sur tesla.com, dans la même session. Tesla ne rattache pas un parrainage à une commande déjà passée.'
  },
  {
    question: 'Quel est l\'avantage aujourd\'hui ?',
    answer: '1 000 km de recharge gratuite sur les Superchargeurs Tesla pour une Model 3 ou Model Y neuve commandée via le lien. Tesla ajuste le programme plusieurs fois par an.'
  },
  {
    question: 'Où voir que le parrainage est bien appliqué ?',
    answer: 'Dans votre compte Tesla, rubrique Parrainage / Loot Box, une fois la commande passée, puis dans vos crédits de recharge après la livraison.'
  },
  {
    question: 'Le parrainage se cumule-t-il avec le bonus écologique ou les aides ?',
    answer: 'Oui. Le parrainage est un avantage Tesla indépendant des aides publiques, les deux se cumulent.'
  },
  {
    question: 'Ce site prend-il ma commande ?',
    answer: 'Non. Ce site ne fait que partager un lien de parrainage. La commande, le paiement et la livraison se font exclusivement sur tesla.com.'
  }
]

// Date of the last check of the program terms shown on the page
const verifiedOn = '12 septembre 2026'
// Tesla caps order referrals at 10 per referrer per calendar year: update after each successful referral
const referralsMax = 10
const referralsLeft = 7

useSEO({
  faq,
  lang: 'fr',
  title: 'Parrainage Tesla : 1 000 km de Supercharge offerts',
  description: 'Commandez votre Tesla avec mon lien de parrainage officiel et recevez 1 000 km de recharge gratuite sur les Superchargeurs. Aucun surcoût.'
})
</script>

<style scoped>
.home {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.hero {
  text-align: center;
  margin-bottom: 3rem;
}

h1 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle {
  font-size: 1.25rem;
  color: #666;
  margin-bottom: 2rem;
}

.referral-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.referral-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  color: white;
}

.referral-card h2 {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: white;
}

.link-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.referral-link {
  flex: 1;
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.referral-link:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.referral-link::selection {
  background: rgba(255, 255, 255, 0.3);
}

.copy-button {
  padding: 1rem 2rem;
  border: 2px solid white;
  border-radius: 8px;
  background: white;
  color: #667eea;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.copy-button:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.copy-button.copied {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.link-description {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 0.95rem;
}

.benefits {
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 12px;
  border-left: 4px solid #667eea;
}

.benefits h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.benefits ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefits li {
  padding: 0.75rem 0;
  font-size: 1.1rem;
  color: #333;
  line-height: 1.6;
}

.share-buttons {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.share-buttons h3 {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #1a1a1a;
  text-align: center;
}

.share-options {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.share-button {
  padding: 1rem 2rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
}

.share-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.share-button.email:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.share-button.whatsapp:hover {
  border-color: #25d366;
  color: #25d366;
}

.share-button.twitter:hover {
  border-color: #1da1f2;
  color: #1da1f2;
}

@media (max-width: 768px) {
  h1 {
    font-size: 2rem;
  }

  .link-container {
    flex-direction: column;
  }

  .copy-button {
    width: 100%;
  }

  .share-options {
    flex-direction: column;
  }

  .share-button {
    width: 100%;
  }
}

.cta-button {
  display: inline-block;
  padding: 1.1rem 2.5rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.35);
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 34px rgba(102, 126, 234, 0.45);
}

.steps {
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 12px;
  border-left: 4px solid #764ba2;
}

.steps h2 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.steps ol {
  margin: 0;
  padding-left: 1.5rem;
  line-height: 1.7;
}

.steps li {
  padding: 0.35rem 0;
}

.trust {
  color: #555;
  line-height: 1.6;
  text-align: center;
  font-style: italic;
}

.hero-note {
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #777;
}

.warning {
  background: #fff7ed;
  border-left: 4px solid #f59e0b;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  color: #7c2d12;
  line-height: 1.6;
}

.benefits h2 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.faq h2 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.faq details {
  border-bottom: 1px solid #e5e7eb;
  padding: 0.75rem 0;
}

.faq summary {
  cursor: pointer;
  font-weight: 600;
  color: #1a1a1a;
}

.faq details p {
  margin: 0.75rem 0 0;
  color: #444;
  line-height: 1.6;
}

.final-cta {
  text-align: center;
}

.disclaimer {
  font-size: 0.8rem;
  color: #888;
  text-align: center;
  line-height: 1.5;
}

.availability {
  background: #ecfdf5;
  border-left: 4px solid #10b981;
  padding: 1rem 1.5rem;
  border-radius: 12px;
}

.availability.low {
  background: #fff7ed;
  border-left-color: #f59e0b;
}

.availability-text {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
  color: #1a1a1a;
}

.availability-text span {
  color: #555;
  font-size: 0.95rem;
}

.availability-bar {
  height: 8px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  overflow: hidden;
}

.availability-fill {
  height: 100%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.availability.low .availability-fill {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}
</style>
